import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOrder } from '../../contexts/OrderContext';
import Card from '../../components/ui/Card';
import PaymentReceipt from '../../components/payment/PaymentReceipt';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle } = FiIcons;

const PaymentReceiptPage = () => {
  const { orderId } = useParams();
  const { getOrderById } = useOrder();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch order details
    const fetchOrder = () => {
      try {
        let orderData = null;
        
        // If orderId is provided in URL, use it
        if (orderId) {
          orderData = getOrderById(orderId);
          if (orderData) {
            console.log("Found order by URL param:", orderData);
            setOrder(orderData);
          } else {
            console.log("Order not found by URL param:", orderId);
          }
        } 
        
        // If no order found by URL param, try session storage
        if (!orderData) {
          const storedOrderId = sessionStorage.getItem('lastCompletedOrderId');
          console.log("Stored order ID from session:", storedOrderId);
          
          if (storedOrderId) {
            orderData = getOrderById(storedOrderId);
            if (orderData) {
              console.log("Found order by session storage:", orderData);
              setOrder(orderData);
            } else {
              console.log("Order not found by session storage ID:", storedOrderId);
            }
          }
        }
        
        // If still no order found
        if (!orderData) {
          console.log("No order found in either URL or session storage");
          toast.error('Order details not found');
          // Don't navigate away immediately to give toast time to show
          setTimeout(() => navigate('/customer'), 2000);
        }
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error('Error loading receipt');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, getOrderById, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Receipt Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find the payment receipt you're looking for</p>
          <button 
            onClick={() => navigate('/customer')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Payment Receipt</h1>
          <p className="text-gray-600 mt-2">Thank you for your payment</p>
        </motion.div>
        
        <PaymentReceipt order={order} />
      </div>
    </div>
  );
};

export default PaymentReceiptPage;